import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Eye, Heart, Loader2, BadgeCheck, Tv, Radio } from "lucide-react";

function RankList({ title, icon: Icon, items, metricLabel, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-border ${accent}`}>
        <Icon size={18} /><h3 className="font-heading font-bold">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">Pas encore de données cette semaine.</p>
      ) : items.map((m, i) => (
        <Link key={m.id} to={`/${m.type}/${m.slug}`} data-testid={`rank-item-${m.slug}`}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0 hover:bg-secondary">
          <span className={`font-heading font-black text-lg w-7 text-center shrink-0 ${i === 0 ? "text-rdcgold" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-muted-foreground"}`}>{i + 1}</span>
          <img src={m.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=00A3E0&color=fff`} alt="" className="w-10 h-10 rounded-lg bg-white shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate flex items-center gap-1">{m.name} {m.verified && <BadgeCheck size={13} className="text-rdcblue" />}</p>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">{m.type === "tv" ? <Tv size={11} /> : <Radio size={11} />} {m.province}</p>
          </div>
          <span className="font-heading font-bold text-sm shrink-0">{m.metric.toLocaleString()}<span className="text-[10px] text-muted-foreground font-normal ml-1">{metricLabel}</span></span>
        </Link>
      ))}
    </div>
  );
}

export default function Rankings() {
  const [provinces, setProvinces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [province, setProvince] = useState("all");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/provinces").then((r) => setProvinces(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(() => {
    const sp = new URLSearchParams({ period: "week" });
    if (province && province !== "all") sp.set("province", province);
    if (category && category !== "all") sp.set("category", category);
    setData(null);
    api.get(`/rankings?${sp.toString()}`).then((r) => setData(r.data));
  }, [province, category]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="rounded-3xl border border-border bg-gradient-to-br from-rdcgold/15 to-rdcblue/10 p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-2 text-rdcgold mb-1"><Trophy size={20} /> <span className="text-sm font-mono uppercase tracking-wider">Palmarès de la semaine</span></div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Classement des médias</h1>
        <p className="text-sm text-muted-foreground mt-1">Les médias les plus regardés/écoutés et les plus soutenus ces 7 derniers jours.</p>
        <div className="mt-4 max-w-xs">
          <Select value={province} onValueChange={setProvince}>
            <SelectTrigger data-testid="ranking-province-select"><SelectValue placeholder="Toutes provinces" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes les provinces</SelectItem>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar">
          {[{ id: "all", name: "Général" }, ...categories].map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id === "all" ? "all" : c.name)} data-testid={`ranking-cat-${c.id === "all" ? "all" : c.slug}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${(category === "all" && c.id === "all") || category === c.name ? "bg-rdcgold text-amber-950" : "bg-card border border-border text-muted-foreground hover:border-rdcgold/50"}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" size={30} /></div> : (
        <div className="grid md:grid-cols-2 gap-4">
          <RankList title="Les plus regardés / écoutés" icon={Eye} items={data.top_watched} metricLabel="vues" accent="text-rdcblue" />
          <RankList title="Les plus soutenus" icon={Heart} items={data.top_supported} metricLabel="$" accent="text-rdcred" />
        </div>
      )}
    </div>
  );
}
